import type { DividerValue, EditorElement } from '@vedalib/editor/lib/elements'
import React, { useMemo } from 'react'
import { chainName } from 'utils'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import type { IFieldConfig } from 'components/formKit'
import { FieldInput, FieldSegmented, FieldSelect, FormField } from 'components/formKit'
import { useFormValues } from 'components/formKit/hooks'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import type { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import * as s from './Divider.module.scss'
import { useDividerNumber } from './DividerElement.hooks'
import type { DividerValueFormType } from './dividerTypes'
import { getDividerNumber } from './utils'

const usePlaceholderNumber = ({ name }: IFieldConfig<Block>) => {
  const element = useFormValues<EditorElement<DividerValue>>(name, 2)
  const placeholder = useDividerNumber(element?.id || '')
  const hidden = !element?.value.isNumbered

  return useMemo(
    () => ({
      hidden,
      props: { placeholder },
    }),
    [hidden, placeholder],
  )
}

const DIVIDER_TYPE = genElementValueField({
  as: FieldSelect<'solid' | 'dotted' | 'dashed'>,
  name: 'dividerType',
  label: t('input.label.type'),
  props: {
    fluid: true,
    options: [
      { value: 'solid', label: t('input.option.solid') },
      { value: 'dotted', label: t('input.option.dotted') },
      { value: 'dashed', label: t('input.option.dashed') },
    ],
  },
})

const DIVIDER_VIEW = genElementValueField({
  as: FieldSegmented<'single' | 'double'>,
  name: 'dividerView',
  label: t('input.label.view'),
  props: {
    options: [
      { value: 'single', label: t('input.option.single') },
      { value: 'double', label: t('input.option.double') },
    ],
  },
})

const IS_NUMBERED = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'isNumbered',
  label: t('input.label.numbered'),
  defaultValue: false,
})

const DIVIDER_NUMBER = genElementValueField({
  as: FieldInput,
  label: t('elements.divider.form.number'),
  name: 'dividerNumber',
  props: { maxLength: 8, parser: getDividerNumber },
  useConfigs: [usePlaceholderNumber],
})

const DividerForm: DividerValueFormType = ({ name, element }) => {
  const isNumberedDivider = element.value.isNumbered

  return (
    <Padding bottom gap sides>
      <FormField config={chainName(name, DIVIDER_TYPE)} />
      <FormField config={chainName(name, DIVIDER_VIEW)} />
      <FormField config={chainName(name, IS_NUMBERED)} />
      {isNumberedDivider && (
        <div className={s.numberExplanationWrapper}>
          <Icon name='builderTooltip' size={KitSize.S} />
          <div className={s.numberExplanation}>{t('elements.divider.form.numberHint')}</div>
        </div>
      )}
      <FormField config={chainName(name, DIVIDER_NUMBER)} />
    </Padding>
  )
}

export default DividerForm
